package link.erpango.quip.domain.extraction

/**
 * Confidence of an extracted field, from most to least reliable.
 */
enum class ExtractionConfidence { HIGH, MEDIUM, LOW }

/**
 * Character span inside the original note where a value was found.
 * `start` is inclusive and `end` is exclusive (Kotlin substring convention).
 */
data class SourceSpan(val start: Int, val end: Int) {

    init {
        require(start >= 0 && start <= end) { "Invalid span: $this" }
    }

    val center: Int get() = (start + end) / 2

    /** The original text covered by this span. */
    fun quote(text: String): String = text.substring(start, end)

    fun overlaps(other: SourceSpan): Boolean = start < other.end && other.start < end

    /** Distance between the centers of both spans. */
    fun centerDistanceTo(other: SourceSpan): Int = kotlin.math.abs(center - other.center)
}

/**
 * A value extracted from a note, with its confidence and where it came from so the
 * review UI can quote the source text.
 */
data class ExtractedField<T>(
    val value: T,
    val confidence: ExtractionConfidence,
    val span: SourceSpan,
)

/**
 * Vehicle candidate detected in a note. Every field is optional: `null` means the parser
 * did not find it. Field values are quoted from the note (see [SourceSpan]).
 */
data class CandidateVehicle(
    val brand: ExtractedField<String>? = null,
    val model: ExtractedField<String>? = null,
    val year: ExtractedField<Int>? = null,
    val plate: ExtractedField<String>? = null,
    val vin: ExtractedField<String>? = null,
    /** Engine oil spec (e.g. "5W30"). Maps to DVehicle.engineOil. */
    val oilSpec: ExtractedField<String>? = null,
    /** Tire size (e.g. "225/45 R17"). Maps to DVehicle.frontTireSize/rearTireSize. */
    val tireSize: ExtractedField<String>? = null,
    /** Fuel type as written ("gasolina", "diésel"...). Maps to DVehicle.recommendedFuel. */
    val fuelType: ExtractedField<String>? = null,
    /** Vehicle type code ("car", "motorcycle"...). Maps to DVehicle.vehicleType. */
    val vehicleType: ExtractedField<String>? = null,
    /** Free-text engine details (e.g. "1.6 TDI" or a data sheet displacement). Maps to DVehicle.engineDetails. */
    val engineDetails: ExtractedField<String>? = null,
    /** Number of cylinders from a data sheet ("Cilindros: 4"). Maps to DVehicle.cylinderCount. */
    val cylinderCount: ExtractedField<Int>? = null,
    /** Front tire pressure below 160 km/h, in bar. Maps to DVehicle.frontTirePressureBelow160. */
    val frontTirePressureBelow160: ExtractedField<Double>? = null,
    /** Rear tire pressure below 160 km/h, in bar. Maps to DVehicle.rearTirePressureBelow160. */
    val rearTirePressureBelow160: ExtractedField<Double>? = null,
    /** Front tire pressure above 160 km/h, in bar. Maps to DVehicle.frontTirePressureAbove160. */
    val frontTirePressureAbove160: ExtractedField<Double>? = null,
    /** Rear tire pressure above 160 km/h, in bar. Maps to DVehicle.rearTirePressureAbove160. */
    val rearTirePressureAbove160: ExtractedField<Double>? = null,
    /** First registration date as written ("15/03/2019"). Maps to DVehicle.firstRegistrationDate. */
    val firstRegistrationDate: ExtractedField<String>? = null,
    /** Next ITV date as written ("15/03/2026"). Maps to DVehicle.nextItvDate. */
    val nextItvDate: ExtractedField<String>? = null,
    /** Front bulb type code ("H7", "HI2R"...). Maps to DVehicle.frontBulbType. */
    val frontBulbType: ExtractedField<String>? = null,
    /** Rear bulb type code ("H7", "HI2R"...). Maps to DVehicle.rearBulbType. */
    val rearBulbType: ExtractedField<String>? = null,
    /** Battery specs as written ("AGM 60Ah 540"). Maps to DVehicle.battery. */
    val batterySpecs: ExtractedField<String>? = null,
    /** Coolant type ("G12", "G13"...). Maps to DVehicle.coolantType. */
    val coolantType: ExtractedField<String>? = null,
)

/**
 * Spare part candidate detected in a note by matching catalog category/type keywords.
 */
data class CandidateSparePart(
    /** The matched phrase as written in the note (e.g. "filtro de aceite"). */
    val description: ExtractedField<String>,
    /** Catalog category id (e.g. "filters", "brakes"). Null when it could not be inferred. */
    val category: ExtractedField<String>? = null,
    /** Catalog type id (e.g. "oil_filter"). Null when only a generic word matched. */
    val type: ExtractedField<String>? = null,
    /** Nearby known spare-part brand as free text (e.g. "Mann"). */
    val brand: ExtractedField<String>? = null,
    /** Reference number if present (e.g. "W67/2" from "ref. W67/2"). */
    val reference: ExtractedField<String>? = null,
    /** Quantity written next to the part in an invoice line (e.g. 4 for "4 bujías"). */
    val quantity: ExtractedField<Double>? = null,
    /** Unit written next to the quantity ("ud", "L"...); the catalog unit is the fallback. */
    val unit: ExtractedField<String>? = null,
    /** Index into [ExtractionResult.vehicles]; `null` = unassigned ("asignar después"). */
    val vehicleIndex: Int? = null,
)

/**
 * Maintenance candidate detected in a note (best effort).
 */
data class CandidateMaintenance(
    /** The action phrase that triggered the detection (e.g. "cambio de aceite"). */
    val description: ExtractedField<String>,
    /** Raw date text as written ("marzo 2025", "12/03/2025"); normalization happens in review. */
    val date: ExtractedField<String>? = null,
    val kilometers: ExtractedField<Int>? = null,
    val cost: ExtractedField<Double>? = null,
    /** Index into [ExtractionResult.vehicles]; `null` = unassigned ("asignar después"). */
    val vehicleIndex: Int? = null,
    /** Workshop name read from an invoice header (medium confidence). Maps to DMaintenance.workshop. */
    val workshop: ExtractedField<String>? = null,
    /** Mechanic name read from an invoice header (medium confidence). Maps to DMaintenance.mechanic. */
    val mechanic: ExtractedField<String>? = null,
)

/**
 * Result of parsing a note offline. Items that could not be associated to any detected
 * vehicle are kept in the main lists with `vehicleIndex == null`; use [unassignedSpareParts]
 * and [unassignedMaintenances] for the "asignar después" bucket of the review UI.
 */
data class ExtractionResult(
    val vehicles: List<CandidateVehicle>,
    val spareParts: List<CandidateSparePart>,
    val maintenances: List<CandidateMaintenance>,
) {

    /** Spare parts that could not be grouped to a vehicle ("asignar después"). */
    val unassignedSpareParts: List<CandidateSparePart> get() = spareParts.filter { it.vehicleIndex == null }

    /** Maintenances that could not be grouped to a vehicle ("asignar después"). */
    val unassignedMaintenances: List<CandidateMaintenance> get() = maintenances.filter { it.vehicleIndex == null }

    fun sparePartsForVehicle(vehicleIndex: Int): List<CandidateSparePart> =
        spareParts.filter { it.vehicleIndex == vehicleIndex }

    fun maintenancesForVehicle(vehicleIndex: Int): List<CandidateMaintenance> =
        maintenances.filter { it.vehicleIndex == vehicleIndex }
}
